import React, { useEffect, useRef, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { MapPin, Building2, Save, Loader2 } from 'lucide-react';
import { updateDoctor, updateDoctorOfficeAddress, type DoctorDTO } from '@/services/doctors';
import { toast } from 'sonner';
import { formatCep, stripCep } from '@/utils/masks';
import { lookupCep } from '@/lib/cepLookup';

interface DoctorClinicSettingsProps {
  doctor: DoctorDTO;
  onUpdate: (updatedDoctor: DoctorDTO) => void;
}

export function DoctorClinicSettings({ doctor, onUpdate }: DoctorClinicSettingsProps) {
  const [hasClinic, setHasClinic] = useState(doctor.hasClinic || false);
  const [officeCep, setOfficeCep] = useState(doctor.officeCep || '');
  const [officeStreet, setOfficeStreet] = useState(doctor.officeStreet || '');
  const [officeNumber, setOfficeNumber] = useState(doctor.officeNumber || '');
  const [officeComplement, setOfficeComplement] = useState(doctor.officeComplement || '');
  const [officeNeighborhood, setOfficeNeighborhood] = useState(doctor.officeNeighborhood || '');
  const [officeCity, setOfficeCity] = useState(doctor.officeCity || '');
  const [officeState, setOfficeState] = useState(doctor.officeState || '');

  const [isSaving, setIsSaving] = useState(false);
  const [isLookingUpCep, setIsLookingUpCep] = useState(false);

  const officeNumberRef = useRef<HTMLInputElement>(null);
  const cepRequestIdRef = useRef(0);
  const cepAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      cepAbortRef.current?.abort();
    };
  }, []);

  const handleCepChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCep(e.target.value);
    setOfficeCep(formatted);

    const requestId = ++cepRequestIdRef.current;
    cepAbortRef.current?.abort();
    cepAbortRef.current = null;

    const digits = stripCep(formatted);
    if (digits.length !== 8) {
      setIsLookingUpCep(false);
      return;
    }

    const controller = new AbortController();
    cepAbortRef.current = controller;
    setIsLookingUpCep(true);

    try {
      const result = await lookupCep(formatted, { signal: controller.signal });
      if (requestId !== cepRequestIdRef.current) {
        return;
      }
      if (!result) {
        return;
      }
      if (!result.ok) {
        if (result.error === 'not_found') {
          toast.error('CEP não encontrado.');
        } else {
          toast.error('Erro ao buscar o CEP.');
        }
        return;
      }

      setOfficeStreet(result.address.street);
      setOfficeNeighborhood(result.address.neighborhood);
      setOfficeCity(result.address.city);
      setOfficeState(result.address.state);
      if (result.address.complement) {
        setOfficeComplement((prev) => (prev.trim() ? prev : result.address.complement));
      }
      officeNumberRef.current?.focus();
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }
      if (requestId === cepRequestIdRef.current) {
        toast.error('Erro ao buscar o CEP.');
      }
    } finally {
      if (requestId === cepRequestIdRef.current) {
        setIsLookingUpCep(false);
      }
    }
  };

  const handleSave = async () => {
    if (hasClinic) {
      const cleanCep = stripCep(officeCep);
      if (!cleanCep || cleanCep.length !== 8) {
        toast.error('CEP deve conter 8 dígitos.');
        return;
      }
      if (!officeStreet.trim() || !officeNumber.trim() || !officeNeighborhood.trim() || !officeCity.trim() || !officeState.trim()) {
        toast.error('Preencha todos os campos obrigatórios do endereço.');
        return;
      }
      if (officeState.trim().length !== 2) {
        toast.error('O Estado (UF) deve conter 2 letras.');
        return;
      }

      try {
        setIsSaving(true);
        const response = await updateDoctorOfficeAddress(doctor.id, {
          officeCep: cleanCep,
          officeStreet: officeStreet.trim(),
          officeNumber: officeNumber.trim(),
          officeComplement: officeComplement.trim() || undefined,
          officeNeighborhood: officeNeighborhood.trim(),
          officeCity: officeCity.trim(),
          officeState: officeState.trim().toUpperCase(),
        });

        if (response.success && response.data) {
          toast.success('Endereço do consultório atualizado!');
          onUpdate(response.data);
        } else {
          // If the backend returns detailed errors (from ValidationPipe) we could show them, 
          // but our backend usually returns an array of strings in `error.message` or a single string.
          toast.error(typeof response.error === 'string' ? response.error : 'Erro ao atualizar endereço.');
        }
      } catch (error) {
        console.error('Error updating clinic address:', error);
        toast.error('Erro ao salvar endereço do consultório.');
      } finally {
        setIsSaving(false);
      }
    } else {
      // User disabled the clinic, just update hasClinic
      try {
        setIsSaving(true);
        const response = await updateDoctor(doctor.id, {
          hasClinic: false,
        });

        if (response.success && response.data) {
          toast.success('Configurações atualizadas!');
          onUpdate(response.data);
        } else {
          toast.error(typeof response.error === 'string' ? response.error : 'Erro ao atualizar configurações.');
        }
      } catch (error) {
        console.error('Error updating clinic settings:', error);
        toast.error('Erro ao salvar alterações.');
      } finally {
        setIsSaving(false);
      }
    }
  };

  return (
    <Card className="border-gray-200 dark:border-neutral-800 shadow-xs">
      <CardHeader className="border-b border-gray-100 dark:border-neutral-800 bg-gray-50/50 dark:bg-neutral-800/30">
        <div className="flex items-center gap-2">
          <Building2 className="w-5 h-5 text-teal-600" />
          <CardTitle className="text-lg font-bold text-gray-900 dark:text-white">
            Configurações de Atendimento
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="p-6 space-y-6">
        <div className="flex items-center justify-between p-4 bg-teal-50/50 dark:bg-teal-900/10 rounded-xl border border-teal-100 dark:border-teal-900/30">
          <div className="space-y-0.5">
            <Label className="text-sm font-bold text-gray-900 dark:text-white cursor-pointer" htmlFor="clinic-toggle">
              Possuo consultório próprio
            </Label>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Ative para permitir que pacientes agendem consultas presenciais na sua clínica.
            </p>
          </div>
          <Switch
            id="clinic-toggle"
            checked={hasClinic}
            onCheckedChange={setHasClinic}
          />
        </div>

        {hasClinic && (
          <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
            <Label className="text-sm font-bold text-gray-700 dark:text-gray-300 flex items-center gap-2 mb-2">
              <MapPin className="w-4 h-4 text-teal-600" />
              Endereço Estruturado do Consultório
            </Label>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="office-cep" className="text-sm text-gray-600 dark:text-gray-400">CEP *</Label>
                <div className="relative">
                  <Input
                    id="office-cep"
                    placeholder="00000-000"
                    value={officeCep}
                    onChange={handleCepChange}
                    disabled={isLookingUpCep}
                    aria-busy={isLookingUpCep}
                    className="bg-white dark:bg-neutral-900 border-gray-200 dark:border-neutral-700 pr-8"
                  />
                  {isLookingUpCep && (
                    <Loader2
                      data-testid="cep-lookup-spinner"
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-teal-600"
                    />
                  )}
                </div>
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="office-street" className="text-sm text-gray-600 dark:text-gray-400">Rua / Avenida *</Label>
                <Input
                  id="office-street"
                  placeholder="Ex: Praça da Sé"
                  value={officeStreet}
                  onChange={(e) => setOfficeStreet(e.target.value)}
                  className="bg-white dark:bg-neutral-900 border-gray-200 dark:border-neutral-700"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="office-number" className="text-sm text-gray-600 dark:text-gray-400">Número *</Label>
                <Input
                  id="office-number"
                  ref={officeNumberRef}
                  placeholder="Ex: 100"
                  value={officeNumber}
                  onChange={(e) => setOfficeNumber(e.target.value)}
                  className="bg-white dark:bg-neutral-900 border-gray-200 dark:border-neutral-700"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="office-complement" className="text-sm text-gray-600 dark:text-gray-400">Complemento</Label>
                <Input
                  id="office-complement"
                  placeholder="Ex: Sala 502"
                  value={officeComplement}
                  onChange={(e) => setOfficeComplement(e.target.value)}
                  className="bg-white dark:bg-neutral-900 border-gray-200 dark:border-neutral-700"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="office-neighborhood" className="text-sm text-gray-600 dark:text-gray-400">Bairro *</Label>
                <Input
                  id="office-neighborhood"
                  placeholder="Ex: Sé"
                  value={officeNeighborhood}
                  onChange={(e) => setOfficeNeighborhood(e.target.value)}
                  className="bg-white dark:bg-neutral-900 border-gray-200 dark:border-neutral-700"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="office-city" className="text-sm text-gray-600 dark:text-gray-400">Cidade *</Label>
                <Input
                  id="office-city"
                  placeholder="Ex: São Paulo"
                  value={officeCity}
                  onChange={(e) => setOfficeCity(e.target.value)}
                  className="bg-white dark:bg-neutral-900 border-gray-200 dark:border-neutral-700"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="office-state" className="text-sm text-gray-600 dark:text-gray-400">Estado (UF) *</Label>
                <Input
                  id="office-state"
                  placeholder="SP"
                  maxLength={2}
                  value={officeState}
                  onChange={(e) => setOfficeState(e.target.value.toUpperCase())}
                  className="bg-white dark:bg-neutral-900 border-gray-200 dark:border-neutral-700 uppercase"
                />
              </div>
            </div>
            
            <p className="text-[11px] text-gray-500 italic mt-2">
              Os campos marcados com * são obrigatórios. Este endereço será exibido para o paciente ao selecionar "Consultório".
            </p>
          </div>
        )}

        <div className="pt-2">
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="w-full md:w-auto bg-teal-600 hover:bg-teal-700 text-white font-bold px-8"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Salvar Configurações
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
